import { reposeDegFromMix } from './profiles';

export function reposeAngleToTan(deg: number): number {
  return Math.tan((deg * Math.PI) / 180);
}

const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** Heights below this (m) are treated as speckle and gently diffused. */
export const SPECKLE_HEIGHT = 0.008;

/** Fraction of a sub-threshold cell's height shed across neighbors per step. */
export const SPECKLE_DIFFUSE_RATE = 0.22;

/**
 * Per-step cap on repose transfer (metres of deposit). Stops a cliff-edge
 * cell from dumping its whole pile onto the floor in a single frame — a
 * ~4 cm ledge pile pours over a few steps instead of teleporting.
 */
export const CLIFF_FLUX_CAP = 0.008;

function surfaceAt(
  deposit: number,
  base: Float32Array | null | undefined,
  i: number,
  film: Float32Array | null | undefined = null,
): number {
  return deposit + (base?.[i] ?? 0) + (film?.[i] ?? 0);
}

/**
 * One mass-conserving thermal-erosion step, plus gentle diffusion of
 * sub-threshold speckles (lone low bumps that repose cannot move).
 *
 * Repose compares TOTAL surface elevation (base + granular + film) between
 * 4-neighbors and moves only GRANULAR mass (`heights`). Film is a settled
 * dust coating — it never avalanches and is never speckle-diffused. A
 * box-edge texel sees a ~1 m drop and pours onto the floor texel below.
 * Transfer is capped at `fluxCap` so the pour animates rather than
 * teleporting.
 *
 * Speckle diffusion: cells with 0 < granular < SPECKLE_HEIGHT shed
 * `SPECKLE_DIFFUSE_RATE * h / 4` to each in-bounds neighbor. Established
 * piles (h ≥ threshold) never act as diffusion sources. All fluxes are
 * computed from the input field, then applied — no in-place races.
 *
 * `film` is read-only (not mutated, not returned). Pass the A-channel
 * analog so slope comparison sees the coating; omit it to match the
 * pre-split kernel.
 *
 * Returns new arrays; input is never mutated.
 */
export function reposeStep(
  heights: Float32Array,
  weighted: Float32Array,
  size: number,
  cellSize: number,
  relax = 0.4,
  speckleHeight = SPECKLE_HEIGHT,
  diffuseRate = SPECKLE_DIFFUSE_RATE,
  base: Float32Array | null = null,
  fluxCap = CLIFF_FLUX_CAP,
  film: Float32Array | null = null,
): { heights: Float32Array; weighted: Float32Array } {
  const n = size * size;
  const outH = new Float32Array(heights);
  const outW = new Float32Array(weighted);
  if (size <= 1 || cellSize <= 0) return { heights: outH, weighted: outW };

  const fluxH = new Float32Array(n * 4);
  const fluxW = new Float32Array(n * 4);
  const cap = fluxCap > 0 ? fluxCap : Number.POSITIVE_INFINITY;

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const i = y * size + x;
      const h = heights[i];
      if (h <= 1e-8) continue;
      const total = h + (film?.[i] ?? 0);
      const mix = total > 1e-8 ? weighted[i] / total : 0;
      const maxDh = reposeAngleToTan(reposeDegFromMix(mix)) * cellSize;
      const s = surfaceAt(h, base, i, film);

      let best = -1;
      let bestS = s;
      for (let k = 0; k < 4; k += 1) {
        const nx = x + NEIGHBORS[k][0];
        const ny = y + NEIGHBORS[k][1];
        if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
        const j = ny * size + nx;
        const nS = surfaceAt(heights[j], base, j, film);
        if (nS < bestS) {
          bestS = nS;
          best = k;
        }
      }
      if (best >= 0) {
        const excess = s - bestS - maxDh;
        if (excess > 0) {
          const transfer = Math.min(excess * relax, h, cap);
          fluxH[i * 4 + best] += transfer;
          fluxW[i * 4 + best] += transfer * mix;
        }
      }

      // Speckle diffusion: only sub-threshold cells shed (piles untouched).
      if (h < speckleHeight && diffuseRate > 0) {
        let nCount = 0;
        for (let k = 0; k < 4; k += 1) {
          const nx = x + NEIGHBORS[k][0];
          const ny = y + NEIGHBORS[k][1];
          if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
          nCount += 1;
        }
        if (nCount > 0) {
          const share = (h * diffuseRate) / nCount;
          for (let k = 0; k < 4; k += 1) {
            const nx = x + NEIGHBORS[k][0];
            const ny = y + NEIGHBORS[k][1];
            if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
            fluxH[i * 4 + k] += share;
            fluxW[i * 4 + k] += share * mix;
          }
        }
      }
    }
  }

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const i = y * size + x;
      for (let k = 0; k < 4; k += 1) {
        const fH = fluxH[i * 4 + k];
        if (fH === 0) continue;
        const nx = x + NEIGHBORS[k][0];
        const ny = y + NEIGHBORS[k][1];
        const j = ny * size + nx;
        outH[i] -= fH;
        outW[i] -= fluxW[i * 4 + k];
        outH[j] += fH;
        outW[j] += fluxW[i * 4 + k];
      }
    }
  }

  return { heights: outH, weighted: outW };
}

export function fieldMass(heights: Float32Array): number {
  let s = 0;
  for (let i = 0; i < heights.length; i += 1) s += heights[i];
  return s;
}

/** Total visual mass = granular (R) + film (A). Film still counts in the ledger. */
export function fieldMassTotal(granular: Float32Array, film?: Float32Array | null): number {
  return fieldMass(granular) + (film ? fieldMass(film) : 0);
}

export function maxSlopeTan(heights: Float32Array, size: number, cellSize: number): number {
  let maxTan = 0;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const h = heights[y * size + x];
      for (let k = 0; k < 4; k += 1) {
        const nx = x + NEIGHBORS[k][0];
        const ny = y + NEIGHBORS[k][1];
        if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
        const dh = h - heights[ny * size + nx];
        if (dh > 0) maxTan = Math.max(maxTan, dh / cellSize);
      }
    }
  }
  return maxTan;
}

/** GLSL body of the repose + speckle-diffusion kernel, kept in lockstep with `reposeStep`. */
export const REPOSE_FRAG = /* glsl */ `
uniform sampler2D tHeight;
uniform sampler2D tBase;
uniform float uTexSize;
uniform float uCellSize;
uniform float uRelax;
uniform float uSpeckleH;
uniform float uDiffuseRate;
uniform float uFluxCap;
varying vec2 vUv;

float reposeTanFromMix(float mixId) {
  float t = clamp(mixId, 0.0, 1.0);
  float deg = t < 0.5
    ? 33.0 + 3.0 * (t * 2.0)
    : 36.0 + 4.0 * ((t - 0.5) * 2.0);
  return tan(radians(deg));
}

float surfaceAt(vec2 uv) {
  vec4 t = texture2D(tHeight, uv);
  // R = granular, A = film (excluded from transfer; still part of the surface).
  return t.r + t.a + texture2D(tBase, uv).r;
}

void main() {
  vec2 texel = vec2(1.0 / uTexSize);
  vec4 self = texture2D(tHeight, vUv);
  float h = self.r;
  float selfS = h + self.a + texture2D(tBase, vUv).r;
  float selfTotal = h + self.a;
  float mixId = selfTotal > 1.0e-8 ? self.g / selfTotal : 0.0;
  float maxDh = reposeTanFromMix(mixId) * uCellSize;

  vec2 nUV[4];
  nUV[0] = vUv + vec2(texel.x, 0.0);
  nUV[1] = vUv + vec2(-texel.x, 0.0);
  nUV[2] = vUv + vec2(0.0, texel.y);
  nUV[3] = vUv + vec2(0.0, -texel.y);

  float bestS = selfS;
  int best = -1;
  int nCount = 0;
  for (int k = 0; k < 4; k++) {
    vec2 uvn = nUV[k];
    if (uvn.x < 0.0 || uvn.x > 1.0 || uvn.y < 0.0 || uvn.y > 1.0) continue;
    nCount += 1;
    float nS = surfaceAt(uvn);
    if (nS < bestS) {
      bestS = nS;
      best = k;
    }
  }

  // Empty cells still run inflow so cliff-pour mass is not dropped.
  float outH = h;
  float outW = self.g;
  if (h > 1.0e-8 && best >= 0) {
    float excess = selfS - bestS - maxDh;
    if (excess > 0.0) {
      float transfer = min(min(excess * uRelax, h), uFluxCap);
      outH -= transfer;
      outW -= transfer * mixId;
    }
  }

  // Speckle outflow: sub-threshold deposit cells shed a fraction to each neighbor.
  if (h > 1.0e-8 && h < uSpeckleH && uDiffuseRate > 0.0 && nCount > 0) {
    float share = (h * uDiffuseRate) / float(nCount);
    outH -= share * float(nCount);
    outW -= share * float(nCount) * mixId;
  }

  // Inflow: each neighbor that would pick US as its lowest SURFACE target,
  // plus diffusion mass shed by sub-threshold neighbors toward us.
  for (int k = 0; k < 4; k++) {
    vec2 uvn = nUV[k];
    if (uvn.x < 0.0 || uvn.x > 1.0 || uvn.y < 0.0 || uvn.y > 1.0) continue;
    vec4 nb = texture2D(tHeight, uvn);
    if (nb.r <= 1.0e-8) continue;
    float nTotal = nb.r + nb.a;
    float nMix = nTotal > 1.0e-8 ? nb.g / nTotal : 0.0;
    float nMax = reposeTanFromMix(nMix) * uCellSize;
    float nbS = nb.r + nb.a + texture2D(tBase, uvn).r;

    float lowest = nbS;
    int nBest = -1;
    int nbCount = 0;
    vec2 nn[4];
    nn[0] = uvn + vec2(texel.x, 0.0);
    nn[1] = uvn + vec2(-texel.x, 0.0);
    nn[2] = uvn + vec2(0.0, texel.y);
    nn[3] = uvn + vec2(0.0, -texel.y);
    for (int j = 0; j < 4; j++) {
      vec2 u2 = nn[j];
      if (u2.x < 0.0 || u2.x > 1.0 || u2.y < 0.0 || u2.y > 1.0) continue;
      nbCount += 1;
      float hhS = surfaceAt(u2);
      if (hhS < lowest) {
        lowest = hhS;
        nBest = j;
      }
    }
    // Neighbor k's opposite direction is us: 0↔1, 2↔3.
    int towardUs = k == 0 ? 1 : k == 1 ? 0 : k == 2 ? 3 : 2;
    if (nBest == towardUs) {
      float excess = nbS - lowest - nMax;
      if (excess > 0.0) {
        float transfer = min(min(excess * uRelax, nb.r), uFluxCap);
        outH += transfer;
        outW += transfer * nMix;
      }
    }
    if (nb.r < uSpeckleH && uDiffuseRate > 0.0 && nbCount > 0) {
      float share = (nb.r * uDiffuseRate) / float(nbCount);
      outH += share;
      outW += share * nMix;
    }
  }

  gl_FragColor = vec4(max(outH, 0.0), outW, self.b, self.a);
}
`;
